<script setup>
import { ref, computed, watch } from 'vue';
import { Head } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useFoodflowDashboardStore } from '@/stores/foodflowDashboard';
import { categoryIcon } from '@/utils/foodCategoryIcons';
import { trans } from 'laravel-vue-i18n';
import FoodFlowPage from '@/components/FoodFlow/FoodFlowPage.vue';

const { search } = useFoodflowDashboardStore();
const { summary } = storeToRefs(useFoodflowDashboardStore());

const period = ref(summary.value?.period ?? 'week');

const loadSummary = () => {
    search({ period: period.value });
};

watch(period, loadSummary);
if (!summary.value) {
    loadSummary();
}

const formatNumber = (value) => Math.round(value ?? 0).toLocaleString();
const signed = (value) => `${value >= 0 ? '+' : ''}${formatNumber(value)}`;

const maxOfThree = computed(() => {
    if (!summary.value) {
        return 1;
    }
    return Math.max(
        summary.value.intake_total,
        summary.value.outtake_total,
        Math.abs(summary.value.net_movement)
    ) || 1;
});

const barPercentage = (value) => Math.min(100, Math.round(Math.abs(value) / maxOfThree.value * 100));

const maxCategoryWeight = computed(() => {
    const weights = summary.value?.category_breakdown?.map(c => c.weight) ?? [];
    return Math.max(...weights, 0) || 1;
});

const categoryBarPercentage = (weight) => Math.round(weight / maxCategoryWeight.value * 100);

const previousComparisonLabel = computed(() => {
    if (!summary.value) {
        return '';
    }
    const value = signed(summary.value.previous_net_movement) + ' ' + trans('label.lbs');
    return trans(`label.vs_last_${period.value}`, { value });
});

const periodPhrase = computed(() => trans(`label.period_this_${period.value}`));
const categoryHeading = computed(() => `${trans('label.intake_by_category')} — ${trans(`label.this_${period.value}`)}`);
const netMovementArrow = computed(() => summary.value?.net_movement >= 0 ? '↑' : '↓');
</script>

<template>
<FoodFlowPage max-width-class="max-w-[1100px]">
    <Head :title="$t('label.foodflow')" />
    <template #title>{{ $t('label.dashboard') }}</template>
    <template #actions>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.period') }}</span>
            <select v-model="period" class="foodflow-filter-select">
                <option value="week">{{ $t('label.this_week') }}</option>
                <option value="month">{{ $t('label.this_month') }}</option>
                <option value="quarter">{{ $t('label.this_quarter') }}</option>
            </select>
        </div>
    </template>

    <template v-if="summary">
        <div class="dashboard-hero">
            <div>
                <div class="dashboard-hero-label">{{ $t('label.current_inventory') }}</div>
                <div>
                    <span class="dashboard-hero-value">{{ formatNumber(summary.current_inventory) }}</span>
                    <span class="dashboard-hero-unit">{{ $t('label.lbs_on_hand') }}</span>
                </div>
                <div class="dashboard-hero-net">
                    <span class="dashboard-hero-net-value" :class="summary.net_movement < 0 ? 'text-[#f0b0ac]' : 'text-[#82c46a]'">
                        {{ netMovementArrow }} {{ formatNumber(Math.abs(summary.net_movement)) }} {{ $t('label.lbs') }}
                    </span>
                    <span class="dashboard-hero-net-caption">{{ $t('label.net') }} {{ periodPhrase }}</span>
                </div>
            </div>
            <div class="dashboard-hero-counters">
                <div class="dashboard-hero-counter">
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    <div class="dashboard-hero-counter-icon bg-[#61B245]/25">📥</div>
                    <div>
                        <div class="dashboard-hero-counter-value">{{ summary.intake_count }}</div>
                        <div class="dashboard-hero-counter-label">{{ $t('label.intake_sessions') }}</div>
                    </div>
                </div>
                <div class="dashboard-hero-counter">
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    <div class="dashboard-hero-counter-icon bg-[#F06660]/25">📤</div>
                    <div>
                        <div class="dashboard-hero-counter-value">{{ summary.outtake_count }}</div>
                        <div class="dashboard-hero-counter-label">{{ $t('label.outtake_sessions') }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="dashboard-stats">
            <div class="dashboard-stat-card">
                <div class="dashboard-stat-label text-[#2d6b1a]">{{ $t('label.total_intake') }}</div>
                <div class="dashboard-stat-value text-[#2d6b1a]">{{ formatNumber(summary.intake_total) }}</div>
                <div class="dashboard-stat-caption">{{ $t('label.lbs_received') }} {{ periodPhrase }}</div>
                <div class="dashboard-stat-bar"><div class="dashboard-stat-bar-fill bg-[#61B245]" :style="{ width: barPercentage(summary.intake_total) + '%' }" /></div>
                <div class="dashboard-stat-footnote">{{ summary.supplier_count }} {{ $t('label.suppliers') }}</div>
            </div>
            <div class="dashboard-stat-card">
                <div class="dashboard-stat-label text-[#a83430]">{{ $t('label.total_outtake') }}</div>
                <div class="dashboard-stat-value text-[#a83430]">{{ formatNumber(summary.outtake_total) }}</div>
                <div class="dashboard-stat-caption">{{ $t('label.lbs_distributed') }} {{ periodPhrase }}</div>
                <div class="dashboard-stat-bar"><div class="dashboard-stat-bar-fill bg-[#F06660]" :style="{ width: barPercentage(summary.outtake_total) + '%' }" /></div>
                <div class="dashboard-stat-footnote">{{ summary.recipient_count }} {{ $t('label.recipients') }}</div>
            </div>
            <div class="dashboard-stat-card">
                <div class="dashboard-stat-label text-[#375475]">{{ $t('label.net_movement') }}</div>
                <div class="dashboard-stat-value text-[#375475]">{{ signed(summary.net_movement) }}</div>
                <div class="dashboard-stat-caption">{{ summary.net_movement >= 0 ? $t('label.net_gain') : $t('label.net_loss') }} {{ periodPhrase }}</div>
                <div class="dashboard-stat-bar"><div class="dashboard-stat-bar-fill bg-[#375475]" :style="{ width: barPercentage(summary.net_movement) + '%' }" /></div>
                <div class="dashboard-stat-footnote">{{ previousComparisonLabel }}</div>
            </div>
        </div>

        <div class="dashboard-section-header">
            <span class="text-[15px] font-semibold text-[#1e2d3d]">{{ categoryHeading }}</span>
        </div>
        <div class="dashboard-category-grid">
            <div v-for="cat in summary.category_breakdown" :key="cat.name" class="dashboard-category-card">
                <div class="flex-shrink-0 text-[24px]">{{ categoryIcon(cat.name) }}</div>
                <div>
                    <div class="text-[12px] font-semibold text-[#1e2d3d]">{{ cat.name }}</div>
                    <div class="dashboard-category-weight">{{ formatNumber(cat.weight) }} {{ $t('label.lbs') }}</div>
                    <div class="dashboard-category-bar"><div class="h-full rounded-sm bg-[#61B245]" :style="{ width: categoryBarPercentage(cat.weight) + '%' }" /></div>
                    <div class="mt-px text-[11px] text-[#64748b]">{{ `${cat.percentage}%` }}</div>
                </div>
            </div>
        </div>
    </template>
</FoodFlowPage>
</template>
